import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';

import type {
    CellClickedEvent,
    CellEditingStartedEvent,
    CellEditingStoppedEvent,
    ColDef,
    ColGroupDef,
    CsvExportParams,
    FirstDataRenderedEvent,
    GetRowIdFunc,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ITooltipParams,
    ModelUpdatedEvent,
    PaginationChangedEvent,
    ProcessCellForExportParams,
    RowClassParams,
    RowClickedEvent,
    RowHeightParams,
    RowStyle,
    SelectionChangedEvent,
    SizeColumnsToContentStrategy,
    SizeColumnsToFitGridStrategy,
    SizeColumnsToFitProvidedWidthStrategy,
    SortChangedEvent,
    ValueFormatterParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
// constants
import { ALL_DROPDOWN_VALUE } from '../../constants/selectCommonOptions';

import { formatNumberFull } from '../../helpers/numeric';
import { sanitizeForExcel } from '../../helpers/sanitize.helper';
import { createShouldForwardProp } from 'helpers/shouldForwardProp';

// hooks
import useSearchAndSetGlobalFilter from '../../hooks/aampeTable/useSearchAndSetGlobalFilter';
//helpers
import useHandleQueryParams from '../../hooks/helper/useHandleQueryParams';
import useRoleFlags from 'hooks/auth/useRoleFlags';

import { DetailCellRendererType } from '../../models/ag-grid.model';
import { LabelAlternateModel } from '../../models/label.model';

// models
import { SearchProps, TableFilterConfig, TableFilterType } from './types';

// components
import BasicButton from '../buttons/basicButton';
import { SearchableSelectType } from '../inputs/searchableSelect';
import GenericSpinner from '../nav/GenericSpinner';
import TableFilters from './TableFilters';

export interface AGTableRef {
    exportCsv: (overrideCSVExportParams?: CsvExportParams) => void;
}

interface AGTableProps<T> {
    rowData?: T[];
    columns: ColDef<T>[] | ColGroupDef<T>[];
    defaultColDef?: ColDef;
    onRowClicked?: (event: RowClickedEvent<T>) => void;
    onCellClicked?: (event: CellClickedEvent<T>) => void;
    allowSearching?: boolean;
    tableName?: string;
    searchProps?: SearchProps;
    isDataLoading?: boolean;
    filtersConfig?: TableFilterConfig[];
    pagination?: boolean;
    autoSizeStrategy?:
        | SizeColumnsToContentStrategy
        | SizeColumnsToFitProvidedWidthStrategy
        | SizeColumnsToFitGridStrategy;
    className?: string;
    csvExportParams?: CsvExportParams;
    emptyStateText?: string;
    handleSelectionChange?: (event: SelectionChangedEvent<any>) => void;
    selectedRowsRef?: LabelAlternateModel[];
    rowStyle?: RowStyle;
    getRowStyle?: (params: RowClassParams<any, any>) => RowStyle | undefined;
    customGridOptions?: GridOptions<T>;
    customOnFirstData?: (event: FirstDataRenderedEvent) => void;
    customOnModelUpdated?: (
        event: ModelUpdatedEvent<LabelAlternateModel>,
    ) => void;
    customHeaderRowStyle?: React.CSSProperties;
    tableWrapperStyle?: React.CSSProperties;
    parentContainerStyle?: React.CSSProperties;
    tableStyle?: React.CSSProperties;
    suppressRowClickSelection?: boolean;
    rowSelection?: 'single' | 'multiple' | undefined;
    detailCellRendererParams?: DetailCellRendererType<any, any>;
    customTableStyle?: Record<string, string>;
    rowHeight?: number;
    getRowHeight?: (params: RowHeightParams) => number;
    masterDetail?: boolean;
    detailRowAutoHeight?: boolean;
    isCustomSorting?: boolean;
    sortOptions?: Record<string, { sort_by: string; is_sorted_desc: string }>;
    suppressCellFocus?: boolean;
    onCellKeyDown?: any;
    onCellEditingStarted?: (event: CellEditingStartedEvent) => void;
    onCellEditingStopped?: (event: CellEditingStoppedEvent) => void;
    onGridApiReady?: (api: GridApi) => void;
    isCellDataEllipsis?: boolean;
    isNoPaddingMargin?: boolean;
    onFilterChanged?: (api: GridApi) => void;
    onGridReady?: (event: GridReadyEvent) => void;
    customActions?: React.ReactNode;
    leftCustomActions?: React.ReactNode;
    getRowClass?: (params: RowClassParams) => string | undefined;
    pinnedTopRowData?: T[];
    defaultPageSize?: number;
    paginationPageSizeSelector?: boolean | number[];
    onFirstDataRendered?: (event: FirstDataRenderedEvent<T>) => void;
    getRowId?: GetRowIdFunc<T>;
}

type AGTableComponent<T = any> = React.ForwardRefExoticComponent<
    React.PropsWithoutRef<AGTableProps<T>> & React.RefAttributes<AGTableRef>
>;

interface TableContainerProps {
    customHeaderRowStyle?: React.CSSProperties;
    tableStyle?: React.CSSProperties;
    customTableStyle?: React.CSSProperties;
    isNoPaddingMargin?: React.CSSProperties;
}
const shouldForwardTableProps = createShouldForwardProp([
    'customHeaderRowStyle',
    'tableStyle',
    'customTableStyle',
    'isNoPaddingMargin',
]);

const defaultNumberFormatter = (params: ValueFormatterParams) => {
    const v = params.value;
    if (v === null || v === undefined || v === '') return v;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? formatNumberFull(n) : v;
};

const AGTable: AGTableComponent = forwardRef<AGTableRef, AGTableProps<any>>(
    (
        {
            rowData,
            columns,
            defaultColDef,
            onRowClicked,
            onCellClicked,
            allowSearching,
            tableName,
            searchProps,
            filtersConfig,
            pagination = false,
            autoSizeStrategy,
            className,
            isDataLoading,
            csvExportParams,
            emptyStateText = 'No rows to show.',
            selectedRowsRef,
            rowStyle = {},
            getRowStyle: parentGetRowStyle,
            handleSelectionChange = () => {},
            customGridOptions = {},
            customOnFirstData,
            customOnModelUpdated,
            customHeaderRowStyle,
            tableWrapperStyle,
            parentContainerStyle,
            tableStyle,
            suppressRowClickSelection = true,
            rowSelection = 'multiple',
            detailCellRendererParams,
            customTableStyle,
            rowHeight,
            masterDetail,
            detailRowAutoHeight,
            isCustomSorting,
            sortOptions,
            suppressCellFocus = true,
            onCellKeyDown,
            onCellEditingStarted,
            onCellEditingStopped,
            onGridApiReady,
            isCellDataEllipsis,
            isNoPaddingMargin,
            onFilterChanged,
            onGridReady,
            customActions,
            leftCustomActions,
            getRowClass,
            getRowHeight,
            pinnedTopRowData,
            defaultPageSize = 25,
            paginationPageSizeSelector = [5, 10, 25, 50, 100],
            onFirstDataRendered,
            getRowId,
        },
        ref,
    ) => {
        const [queryParams, setQueryParams, removeQueryParams] =
            useHandleQueryParams();
        const { hasWriteAccess } = useRoleFlags();
        const gridRef = useRef<GridApi>();

        const pageSize = useMemo<number>(() => {
            const { page_size } = queryParams;
            if (page_size) {
                return parseInt(page_size as string);
            }
            return defaultPageSize;
        }, [queryParams, defaultPageSize]);

        const setInitialQuickFilter = (
            api: GridApi,
            search: string | undefined,
        ) => {
            if (search) {
                api.updateGridOptions({ quickFilterText: search });
            }
        };

        const setInitialColumnSort = (
            api: GridApi,
            sortBy: string | undefined,
            isSortedDesc: string | undefined,
        ) => {
            if (sortBy) {
                api.applyColumnState({
                    state: [
                        {
                            colId: sortBy,
                            sort: isSortedDesc === 'true' ? 'desc' : 'asc',
                        },
                    ],
                });
            }
        };

        const createFilterModel = (
            item: TableFilterConfig,
            filterValues: string[] | string | number | null,
        ) => {
            if (!filterValues) return null;

            if (
                item.type === TableFilterType.MULTISELECT &&
                Array.isArray(filterValues)
            ) {
                const conditions = filterValues.map((filterValueObj) => ({
                    type: item.meta?.filterType ?? 'contains',
                    filter: filterValueObj,
                }));
                return {
                    filterType: item.cellDataType || 'text',
                    conditions,
                    operator: 'OR',
                };
            }

            return {
                filterType: item.cellDataType || 'text',
                type: item.meta?.filterType ?? 'equals',
                filter: Array.isArray(filterValues)
                    ? filterValues[0]
                    : filterValues,
            };
        };

        const applyInitialFilters = (
            api: GridApi,
            filtersConfig: TableFilterConfig[],
            filterParams: Record<string, any>,
        ) => {
            if (!filtersConfig?.length) return;

            const initialFilterState: Record<string, any> = {};
            filtersConfig.forEach((item) => {
                if (!filterParams[item.field]) return;

                const filterValues = Array.isArray(filterParams[item.field])
                    ? filterParams[item.field]
                    : filterParams[item.field].toString().split(',');

                const filterDef = createFilterModel(item, filterValues);
                initialFilterState[item.field] = filterDef;
            });

            api.setFilterModel(initialFilterState);
        };

        const onReady = (event: GridReadyEvent) => {
            const { api } = event;
            gridRef.current = api;
            // Loading overlay is now controlled exclusively by the useEffect hook
            // that watches isDataLoading and rowData props (see lines 568-577)

            if (onGridApiReady) {
                onGridApiReady(api);
            }
            onGridReady?.(event);
            // set initial state for column sort
            const { search, sort_by, is_sorted_desc } = queryParams;
            if (search) {
                setInitialQuickFilter(api, search as string);
            }
            if (sort_by) {
                setInitialColumnSort(
                    api,
                    sort_by as string,
                    is_sorted_desc as string,
                );
            }
        };

        //this keeps track of the users current selections between labels and re-selects them if they switch between labels
        const onRowDataUpdated = (event: any) => {
            if (selectedRowsRef) {
                selectedRowsRef.forEach((item: { index: any }) => {
                    const rowNode = event.api.getRowNode(item.index);
                    if (rowNode) {
                        rowNode.setSelected(true);
                    }
                });
            }
        };

        const handleSearch = (value: string | undefined) => {
            gridRef.current?.updateGridOptions({
                quickFilterText: value ?? '',
            });
        };

        const handleSortChange = (event: SortChangedEvent) => {
            if (isCustomSorting) return;
            // ag-Grid re-fires onSortChanged with source 'gridOptionsChanged' whenever gridOptions
            // is a new object reference (every React render). This can override the user's sort
            // with a column definition's default sort, so we ignore these spurious events.
            if (event.source === 'gridOptionsChanged') return;
            const columnState = event.api.getColumnState();
            const sortColumn = columnState.find((item) => !!item.sort);
            if (sortColumn) {
                setQueryParams({
                    sort_by: sortColumn.colId,
                    is_sorted_desc: sortColumn.sort === 'desc',
                });
            } else {
                // clear sort params
                removeQueryParams(['sort_by', 'is_sorted_desc']);
            }
        };

        const handlePaginationChange = (event: PaginationChangedEvent) => {
            if (gridRef.current) {
                const { newPage, newPageSize } = event;
                const page = gridRef.current.paginationGetCurrentPage();
                const pageSize = gridRef.current.paginationGetPageSize();
                if (newPage || newPageSize) {
                    setQueryParams({
                        page,
                        page_size: pageSize,
                    });
                }
            }
        };

        useEffect(() => {
            if (gridRef.current && isCustomSorting) {
                const { sort_by, is_sorted_desc } = queryParams;
                gridRef.current.applyColumnState({
                    state: [
                        {
                            colId: sort_by as string,
                            sort: is_sorted_desc === 'true' ? 'desc' : 'asc',
                        },
                    ],
                    defaultState: { sort: null },
                });
            }
        }, [queryParams, isCustomSorting]);

        useEffect(() => {
            const { is_expand } = queryParams;
            if (masterDetail && is_expand) {
                if (is_expand == 'true') {
                    gridRef.current?.forEachNode((node) => {
                        if (node.master) {
                            node.setExpanded(true);
                        }
                    });
                } else {
                    gridRef.current?.forEachNode((node) => {
                        if (node.master) {
                            node.setExpanded(false);
                        }
                    });
                }
            }
        }, [queryParams, masterDetail]);

        const handleFirstTimeDataRendered = (event: FirstDataRenderedEvent) => {
            const { page } = queryParams;
            const { api } = event;
            if (page) {
                event.api.paginationGoToPage(parseInt(page as string));
            }

            // Loading overlay is now controlled exclusively by the useEffect hook
            // that watches isDataLoading and rowData props (see lines 568-577)

            if (filtersConfig) {
                applyInitialFilters(api, filtersConfig, queryParams);
            }

            customOnFirstData && customOnFirstData(event);
        };

        const updateQueryParams = (
            field: string,
            filterValue: string[] | string | number | null,
        ) => {
            if (filterValue && Array.isArray(filterValue)) {
                setQueryParams({
                    [field]: filterValue.join(','),
                });
            } else if (filterValue) {
                setQueryParams({ [field]: filterValue });
            } else {
                removeQueryParams([field]);
            }
        };

        const handleFilterChange = async (
            item: TableFilterConfig,
            value: SearchableSelectType[] | string | number,
        ) => {
            // Early return if no value or ALL_DROPDOWN_VALUE
            if (
                (!value ||
                    value === ALL_DROPDOWN_VALUE ||
                    (Array.isArray(value) && value.length === 0)) &&
                !item.setQueryParamsOnly
            ) {
                await gridRef.current?.setColumnFilterModel(item.field, null);
                gridRef.current?.onFilterChanged();
                removeQueryParams([item.field]);
                return;
            }

            // Transform value to appropriate format
            const filterValue = Array.isArray(value)
                ? value.map((item) => `${item.value}`)
                : value;

            // Handle query params only case
            if (item.setQueryParamsOnly) {
                updateQueryParams(item.field, filterValue);
                return;
            }

            // Apply filter and update query params
            const filterModel = createFilterModel(item, filterValue);
            if (filterModel) {
                await gridRef.current?.setColumnFilterModel(
                    item.field,
                    filterModel,
                );
                gridRef.current?.onFilterChanged();
            }
            updateQueryParams(item.field, filterValue);
        };

        useSearchAndSetGlobalFilter({
            setGlobalFilter: handleSearch,
            queryKey: tableName && `${tableName}-search`,
            debounceTime: 200,
        });

        const getRowStyle = useCallback(
            (params: RowClassParams): RowStyle | undefined => {
                const parentStyles = parentGetRowStyle
                    ? parentGetRowStyle(params)
                    : {};
                const baseStyles = {
                    ...parentStyles,
                };
                if (onRowClicked) {
                    return {
                        ...baseStyles,
                        cursor: 'pointer',
                    };
                }
                return baseStyles;
            },
            [onRowClicked, parentGetRowStyle],
        );

        const sanitizeProcessCell = useCallback(
            (params: ProcessCellForExportParams) => {
                return sanitizeForExcel(params.formatValue(params.value));
            },
            [],
        );

        const handleCSVExport = useCallback(
            (overrideCSVExportParams?: CsvExportParams) => {
                const csvParams = {
                    ...csvExportParams,
                    ...(overrideCSVExportParams ?? {}),
                    processCellCallback: sanitizeProcessCell,
                };

                gridRef.current?.exportDataAsCsv(csvParams);
            },
            [csvExportParams, sanitizeProcessCell],
        );

        // Single source of truth for loading overlay state
        // This useEffect is the ONLY place where overlay state should be controlled
        useEffect(() => {
            if (!gridRef.current) return;

            if (isDataLoading) {
                // Show loading overlay when data is being fetched
                gridRef.current.showLoadingOverlay();
            } else if (!rowData || rowData.length === 0) {
                // Show "no rows" overlay when not loading and no data
                gridRef.current.showNoRowsOverlay();
            } else {
                // Hide overlay when not loading and data exists
                gridRef.current.hideOverlay();
            }
        }, [isDataLoading, rowData]);

        useImperativeHandle(ref, () => ({
            exportCsv: handleCSVExport,
        }));

        useEffect(() => {
            if (gridRef.current) {
                if (pinnedTopRowData && pinnedTopRowData.length) {
                    gridRef.current.updateGridOptions({
                        pinnedTopRowData: pinnedTopRowData,
                    });
                } else {
                    gridRef.current.updateGridOptions({
                        pinnedTopRowData: undefined,
                    });
                }
            }
        }, [pinnedTopRowData]);

        const effectiveDefaultColDef = useMemo(() => {
            const base = (defaultColDef as ColDef) ?? {
                resizable: false,
                tooltipValueGetter: (p: ITooltipParams) => p.value,
                cellStyle: onCellClicked ? { cursor: 'pointer' } : undefined,
            };

            const baseAny = base as ColDef;

            return {
                ...baseAny,
                valueFormatter:
                    baseAny.valueFormatter ?? defaultNumberFormatter,
            } as ColDef;
        }, [defaultColDef, onCellClicked]);

        const effectiveDetailCellRendererParams = useMemo(() => {
            if (!detailCellRendererParams) return detailCellRendererParams;
            return {
                ...detailCellRendererParams,
                detailGridOptions: {
                    suppressContextMenu: true,
                    ...detailCellRendererParams.detailGridOptions,
                },
            };
        }, [detailCellRendererParams]);

        return (
            <Container
                className={className}
                style={parentContainerStyle}
                isCellDataEllipsis={isCellDataEllipsis}
                isNoPaddingMargin={isNoPaddingMargin}
            >
                {csvExportParams && hasWriteAccess && (
                    <ButtonContainer>
                        <BasicButton
                            name="Export as CSV"
                            action={handleCSVExport}
                            isPrimary={false}
                            isDisabled={isDataLoading}
                        />
                    </ButtonContainer>
                )}
                {(!!filtersConfig?.length || allowSearching) && (
                    <TableFilters
                        allowSearching={allowSearching}
                        allowSorting={isCustomSorting}
                        searchProps={searchProps}
                        filtersConfig={filtersConfig}
                        tableName={tableName}
                        handleFilterChange={handleFilterChange}
                        sortOptions={sortOptions ?? {}}
                        className={className}
                        customActions={customActions}
                        leftCustomActions={leftCustomActions}
                    />
                )}

                <TableContainer
                    className="ag-theme-custom"
                    customHeaderRowStyle={customHeaderRowStyle}
                    tableStyle={tableStyle}
                    customTableStyle={customTableStyle}
                >
                    <AgGridReact
                        getRowId={getRowId}
                        rowData={rowData}
                        columnDefs={columns}
                        getRowStyle={getRowStyle}
                        onFirstDataRendered={onFirstDataRendered}
                        getRowClass={getRowClass}
                        onGridReady={onReady}
                        containerStyle={tableWrapperStyle}
                        onCellEditingStarted={onCellEditingStarted}
                        onCellEditingStopped={onCellEditingStopped}
                        gridOptions={{
                            autoSizeStrategy: autoSizeStrategy,
                            domLayout: 'autoHeight',
                            suppressCellFocus: suppressCellFocus,
                            suppressMovableColumns: true,
                            onSortChanged: handleSortChange,
                            headerHeight: 44,
                            columnHoverHighlight: true,
                            pagination,
                            paginationPageSize: pageSize,
                            paginationPageSizeSelector:
                                paginationPageSizeSelector,
                            onPaginationChanged: handlePaginationChange,
                            onFirstDataRendered: handleFirstTimeDataRendered,
                            suppressColumnVirtualisation: true,
                            enableCellTextSelection: true,
                            ensureDomOrder: true,
                            tooltipShowDelay: 600,
                            tooltipHideDelay: 2000,
                            tooltipInteraction: true,
                            overlayNoRowsTemplate: `<span aria-live="polite" aria-atomic="true";pos>${emptyStateText}`,
                            rowStyle: rowStyle,
                            rowSelection: rowSelection,
                            suppressRowClickSelection:
                                suppressRowClickSelection,
                            reactiveCustomComponents: true,
                            suppressContextMenu: true,
                            onSelectionChanged: handleSelectionChange,
                            ...customGridOptions,
                            onModelUpdated: customOnModelUpdated,
                            animateRows:
                                import.meta.env.VITE_CHROMATIC !== 'true',
                            getRowHeight: getRowHeight,
                            pinnedTopRowData,
                        }}
                        className={`css({ width: '100%', height: '100%' })`}
                        onRowClicked={onRowClicked}
                        onCellClicked={onCellClicked}
                        loadingOverlayComponent={GenericSpinner}
                        defaultColDef={effectiveDefaultColDef}
                        onRowDataUpdated={onRowDataUpdated}
                        detailCellRendererParams={
                            effectiveDetailCellRendererParams
                        }
                        masterDetail={masterDetail}
                        detailRowAutoHeight={detailRowAutoHeight}
                        rowHeight={rowHeight}
                        onCellKeyDown={onCellKeyDown}
                        onFilterChanged={(event) => {
                            customGridOptions?.onFilterChanged?.(event);
                            onFilterChanged?.(event.api);
                        }}
                    />
                </TableContainer>
            </Container>
        );
    },
);

const Container = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['isCellDataEllipsis', 'isNoPaddingMargin'].includes(prop),
})<{ isCellDataEllipsis?: boolean; isNoPaddingMargin?: boolean }>((props) => ({
    display: 'flex',
    flexDirection: 'column',
    rowGap: aampeTheme.spacing4(2),
    maxWidth: '100%',
    overflow: 'auto',
    margin: props.isNoPaddingMargin ? aampeTheme.spacing4(0) : '',
    paddingBottom: aampeTheme.spacing4(2),
    padding: props.isNoPaddingMargin
        ? aampeTheme.spacing4(0)
        : aampeTheme.spacing4(4),
    backgroundColor: aampeTheme.colors.white,
    ...(props.style ?? {}),
    ...(props.isCellDataEllipsis && {
        '.ag-cell': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '.ag-cell-value': {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            WebkitLineClamp: 3,
            whiteSpace: 'normal',
        },
    }),
}));

const TableContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardTableProps,
})<TableContainerProps>`
    .ag-body-viewport {
        max-height: calc(100vh - 24rem);
        overflow-y: scroll;
        ${(props) => props.tableStyle && { ...props.tableStyle }}
    }

    .ag-row {
        z-index: 1;
        &:hover {
            z-index: 100;
        }
    }

    .ag-theme-custom {
        --ag-border-color: ${aampeTheme.colors.interactiveWG01};
        z-index: 101;
    }
    .ag-menu {
        z-index: 101;
    }

    .ag-header {
        ${(props) =>
            props.customHeaderRowStyle && { ...props.customHeaderRowStyle }}
    },
   ${(props) => props.customTableStyle && { ...props.customTableStyle }}
`;

const ButtonContainer = styled.div({
    width: '100%',
    justifyContent: 'flex-end',
    display: 'flex',
});

export default React.memo(AGTable);
